import React, { useMemo, useRef, useEffect, useState } from "react";
import Graph from "react-graph-vis";

export const GraphPreview = ({ environment, graphData, colorBackground, colorBorder, colorMainEdge }) => {
  const networkRef = useRef(null);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: '' });
  const [dimensions, setDimensions] = useState({ width: window.innerWidth, height: window.innerHeight });

  console.log("GraphPreview received colorMainEdge:", colorMainEdge);

  // Generar un key único basado en los datos del grafo para forzar re-render completo
  const graphKey = useMemo(() => {
    if (!graphData || !graphData.nodes) return 0;
    return `graph-${graphData.nodes.length}-${Date.now()}`;
  }, [graphData]);

  // Función para obtener las iniciales de un nombre
  const getInitials = (nombre) => {
    return nombre
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  // Función para crear un SVG data URL con las iniciales solamente
  const createInitialsImage = (initials, borderColor) => {
    const svg = `
      <svg width="100" height="100" xmlns="http://www.w3.org/2000/svg">
        <circle cx="50" cy="50" r="50" fill="${borderColor || '#ED0029'}"/>
        <text x="50" y="50" font-family="Arial, sans-serif" font-size="40" font-weight="bold"
              fill="white" text-anchor="middle" dominant-baseline="central">
          ${initials}
        </text>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  // Función para crear imagen con objectFit contain usando SVG
  const createContainImageSVG = (imageUrl, borderColor) => {
    // Crear SVG con estructura de capas
    const svg = `
      <svg width="100" height="100" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
        <!-- Fondo blanco completo -->
        <circle cx="50" cy="50" r="50" fill="white"/>

        <!-- Imagen con preserveAspectRatio para simular object-fit: contain -->
        <!-- La imagen ocupará el 60% del círculo (de 100 a 60) -->
        <image
          xlink:href="${imageUrl}"
          x="20"
          y="20"
          width="60"
          height="60"
          preserveAspectRatio="xMidYMid meet"
        />
      </svg>
    `;

    // Retornar como data URL
    return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
  };

  // Transformar los datos al formato esperado por vis-network
  const graph = useMemo(() => {
    try {
      // Función para generar hash simple de un string
      const simpleHash = (str) => {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
          const char = str.charCodeAt(i);
          hash = ((hash << 5) - hash) + char;
          hash = hash & hash; // Convert to 32bit integer
        }
        return Math.abs(hash).toString(36);
      };

      if (!graphData || !graphData.nodes || !graphData.edges) {
        console.log("Graph data is not ready");
        return { nodes: [], edges: [] };
      }

      // Validar que nodes y edges sean arrays
      if (!Array.isArray(graphData.nodes) || !Array.isArray(graphData.edges)) {
        console.error("Invalid data structure - nodes or edges is not an array");
        return { nodes: [], edges: [] };
      }


    // Deduplicar nodos por ID
    const uniqueNodesMap = new Map();
    graphData.nodes.forEach(node => {
      const nodeId = String(node.id);
      if (!uniqueNodesMap.has(nodeId)) {
        uniqueNodesMap.set(nodeId, node);
      } else {
        console.warn("DUPLICATE FOUND IN SOURCE DATA:", nodeId);
      }
    });
    const uniqueNodes = Array.from(uniqueNodesMap.values());

    // Filtrar edges válidos y agrupar múltiples relaciones
    const edgeMap = new Map();

    graphData.edges
      .filter((edge) => {
        // Verificar que los nodos fuente y destino existan
        const sourceExists = uniqueNodes.some((n) => String(n.id) === String(edge.source));
        const destExists = uniqueNodes.some((n) => String(n.id) === String(edge.destination));
        return sourceExists && destExists;
      })
      .forEach((edge) => {
        const from = String(edge.source);
        const to = String(edge.destination);
        // Crear una clave única para la conexión (ordenada para bidireccional)
        const key = from < to ? `${from}-${to}` : `${to}-${from}`;

        if (!edgeMap.has(key)) {
          edgeMap.set(key, {
            from,
            to,
            types: []
          });
        }

        const edgeData = edgeMap.get(key);
        edgeData.types.push(edge.type);
      });

    // Buscar el nodo principal por coincidencia exacta o parcial ANTES de crear edges
    let mainNodeId = null;
    if (graphData.main) {
      const mainValue = String(graphData.main);
      // Buscar coincidencia exacta primero
      const exactMatch = uniqueNodes.find((n) => String(n.id) === mainValue);
      if (exactMatch) {
        mainNodeId = String(exactMatch.id);
      } else {
        // Buscar coincidencia parcial (ej: main=3345 coincide con id="U3345")
        const partialMatch = uniqueNodes.find((n) => String(n.id).includes(mainValue));
        if (partialMatch) {
          mainNodeId = String(partialMatch.id);
        }
      }
    }

    // Calcular niveles de cada nodo usando BFS desde el nodo principal
    const nodeLevels = new Map();
    if (mainNodeId) {
      nodeLevels.set(mainNodeId, 0);

      // Solo hacer BFS si hay edges
      if (edgeMap.size > 0) {
        const queue = [{ nodeId: mainNodeId, level: 0 }];
        const visited = new Set([mainNodeId]);

        // Crear un grafo temporal para el BFS
        const adjacencyList = new Map();
        edgeMap.forEach((edgeData) => {
          if (!adjacencyList.has(edgeData.from)) adjacencyList.set(edgeData.from, []);
          if (!adjacencyList.has(edgeData.to)) adjacencyList.set(edgeData.to, []);
          adjacencyList.get(edgeData.from).push(edgeData.to);
          adjacencyList.get(edgeData.to).push(edgeData.from);
        });

        while (queue.length > 0) {
          const { nodeId, level } = queue.shift();
          const neighbors = adjacencyList.get(nodeId) || [];

          neighbors.forEach((neighbor) => {
            if (!visited.has(neighbor)) {
              visited.add(neighbor);
              nodeLevels.set(neighbor, level + 1);
              queue.push({ nodeId: neighbor, level: level + 1 });
            }
          });
        }
      }
    }

    // Función para obtener el color según el nivel del edge
    const getEdgeColorByLevel = (fromLevel, toLevel) => {
      if(colorMainEdge){
        return colorMainEdge;
      }
      const maxLevel = Math.max(fromLevel ?? 999, toLevel ?? 999);
      if (maxLevel === 0) {
        console.log("Main edge color:", colorMainEdge || "#000000");
        return colorMainEdge || "#000000"; // Color configurable para edge principal
      }
      if (maxLevel === 1) return "#4B5563"; // Gris oscuro para nivel 1
      return "#D1D5DB"; // Gris claro para nivel 2+
    };

    // Convertir el Map a array de edges
    const transformedEdges = Array.from(edgeMap.values()).map((edgeData, index) => {
      const relationshipCount = edgeData.types.length;

      // Obtener los nombres de los nodos
      const fromNode = uniqueNodes.find(n => String(n.id) === edgeData.from);
      const toNode = uniqueNodes.find(n => String(n.id) === edgeData.to);
      const fromName = fromNode ? fromNode.nombre : edgeData.from;
      const toName = toNode ? toNode.nombre : edgeData.to;

      // Construir el tooltip con todos los tipos de relación
      const tooltipLines = edgeData.types.map((type, idx) => `${idx + 1}. ${type}`);
      const tooltipText = `Relations ${fromName} - ${toName} (${relationshipCount}):\n${tooltipLines.join('\n')}`;

      const fromLevel = nodeLevels.get(edgeData.from);
      const toLevel = nodeLevels.get(edgeData.to);
      const edgeColor = getEdgeColorByLevel(fromLevel, toLevel);

      console.log(`Edge ${edgeData.from} -> ${edgeData.to}: fromLevel=${fromLevel}, toLevel=${toLevel}, color=${edgeColor}`);

      return {
        id: `edge-${index}`, // Usar un ID único string para edges
        from: edgeData.from, // Mantener como string
        to: edgeData.to, // Mantener como string
        title: tooltipText,
        width: relationshipCount, // Grosor según cantidad de relaciones
        color: {
          color: edgeColor,
          highlight: edgeColor,
          hover: edgeColor,
          opacity: 1
        },
        arrows: {
          to: { enabled: false },
          from: { enabled: false }
        },
        relationshipTypes: edgeData.types, // Guardar los tipos para el tooltip personalizado
        fromName: fromName, // Guardar nombre del nodo origen
        toName: toName, // Guardar nombre del nodo destino
      };
    });

    // Crear un Map para contar las relaciones por nodo
    const relationshipCounts = new Map();
    transformedEdges.forEach((edge) => {
      relationshipCounts.set(edge.from, (relationshipCounts.get(edge.from) || 0) + 1);
      relationshipCounts.set(edge.to, (relationshipCounts.get(edge.to) || 0) + 1);
    });

    // Crear un Set de IDs de nodos que tienen al menos una relación
    const nodesWithRelationships = new Set(relationshipCounts.keys());

    // Función para obtener el color según el nivel
    const getColorByLevel = (level) => {
      if (level === 0) return "#000000"; // Negro para nodo principal
      if (level === 1) return "#4B5563"; // Gris oscuro para nivel 1
      return "#D1D5DB"; // Gris claro para nivel 2+
    };

    // Crear Set para verificar duplicados
    const seenIds = new Set();

    const transformedNodes = uniqueNodes
      .filter((node) => {
        const nodeId = String(node.id);
        // Incluir el nodo si tiene relaciones O si es el nodo principal
        return nodesWithRelationships.has(nodeId) || nodeId === mainNodeId;
      })
      .map((node, index) => {
        const nodeId = String(node.id);

        // Verificar si ya vimos este ID
        if (seenIds.has(nodeId)) {
          console.error("DUPLICATE DETECTED in transformedNodes:", nodeId, "at index:", index);
        }
        seenIds.add(nodeId);

        const hasValidImage = node.imagen && node.imagen !== "NONE";
        const initials = getInitials(node.nombre);
        const isMainNode = nodeId === mainNodeId;
        const relationshipCount = relationshipCounts.get(nodeId) || 0;
        // Si es el nodo principal, siempre nivel 0, sino usar el nivel calculado
        const nodeLevel = isMainNode ? 0 : (nodeLevels.get(nodeId) ?? 999);
        const nodeColor = getColorByLevel(nodeLevel);

        // Verificar si el ID comienza con "E" para aplicar contain
        const startsWithE = nodeId.startsWith("E");

        // Generar la imagen apropiada
        let nodeImage;
        if (hasValidImage) {
          if (startsWithE) {
            // Si empieza con E, usar la imagen con contain (SVG)
            nodeImage = node.imagen;
            nodeImage = createInitialsImage(initials, nodeColor);
          } else {
            // Si no empieza con E, usar la imagen normal (cover)
            nodeImage = node.imagen;
          }
        } else {
          // Si no hay imagen válida, usar iniciales
          nodeImage = createInitialsImage(initials, nodeColor);
        }

        // Determinar el tamaño del nodo
        let nodeSize;
        if (isMainNode) {
          nodeSize = 35; // Nodo principal
        } else if (startsWithE && hasValidImage) {
          nodeSize = 40; // Nodos con E más grandes para ver mejor el contain
        } else {
          nodeSize = 20; // Nodos normales
        }

        // Configurar el color de fondo para nodos con E (blanco)
        const nodeBackground = (startsWithE && hasValidImage) ? "#FFFFFF" : (hasValidImage ? "transparent" : nodeColor);

        return {
          id: nodeId, // USAR SOLO EL ID ORIGINAL SIN TRANSFORMACIONES
          title: `${node.nombre}\nID: ${nodeId}\nRelations: ${relationshipCount}`,
          shape: "circularImage",
          image: nodeImage,
          // Si es el nodo principal, colocarlo en el centro
          x: isMainNode ? 0 : undefined,
          y: isMainNode ? 0 : undefined,
          fixed: isMainNode ? { x: true, y: true } : false,
          size: nodeSize,
          borderWidth: 3,
          borderWidthSelected: 4,
          color: {
            border: nodeColor,
            background: nodeBackground,
            highlight: {
              border: nodeColor,
              background: nodeBackground
            }
          },
          shadow: {
            enabled: true,
            color: 'rgba(0, 0, 0, 0.4)',
            size: 15,
            x: 3,
            y: 3
          },
          // Sin label visible
          label: undefined,
        };
      });


      // Verificar duplicados en los IDs finales
      const finalIds = transformedNodes.map(n => n.id);

      return {
        nodes: transformedNodes,
        edges: transformedEdges,
      };
    } catch (error) {
      console.error("Error processing graph data:", error);
      console.error("GraphData:", graphData);
      return { nodes: [], edges: [] };
    }
  }, [graphData, colorBorder, colorMainEdge]);

  const options = {
    layout: {
      hierarchical: false,
    },
    edges: {
      color: "#CCCCCC",
      smooth: {
        type: "continuous",
      },
    },
    nodes: {
      size: 25,
      borderWidth: 2,
      borderWidthSelected: 3,
    },
    physics: {
      enabled: false,
    },
    interaction: {
      dragNodes: true,
      dragView: true,
      zoomView: false,
      hover: true,
      tooltipDelay: 100,
      hideEdgesOnDrag: false,
    },
    tooltips: {
      enabled: true,
    },
    autoResize: true,
    height: environment === "PROD" ? "100%" : "600px",
    width: environment === "PROD" ? "100%" : "100%",
  };

  const events = {
    select: function (event) {
      const { nodes, edges } = event;
      console.log("Selected nodes:", nodes);
      console.log("Selected edges:", edges);
    },
    hoverNode: function (event) {
      const uniqueNodeId = event.node;
      const node = graph.nodes.find(n => n.id === uniqueNodeId);
      if (node) {
        // Extraer información del title que ya tiene el formato correcto
        const content = node.title;
        setTooltip({
          visible: true,
          x: event.pointer.DOM.x,
          y: event.pointer.DOM.y,
          content: content
        });
      }
    },
    blurNode: function () {
      setTooltip({ visible: false, x: 0, y: 0, content: '' });
    },
    hoverEdge: function (event) {
      const edgeId = event.edge;
      const edge = graph.edges.find(e => e.id === edgeId);
      if (edge && edge.relationshipTypes) {
        const content = `Relations ${edge.fromName} - ${edge.toName} (${edge.relationshipTypes.length}):\n${edge.relationshipTypes.map((type, idx) => `${idx + 1}. ${type}`).join('\n')}`;
        setTooltip({
          visible: true,
          x: event.pointer.DOM.x,
          y: event.pointer.DOM.y,
          content: content
        });
      }
    },
    blurEdge: function () {
      setTooltip({ visible: false, x: 0, y: 0, content: '' });
    },
  };

  // Obtener los tipos de relación únicos del grafo (debe estar antes del return condicional)
  const edgeTypes = useMemo(() => {
    if (!graphData || !graphData.edges) return [];

    const types = new Set();
    graphData.edges.forEach((edge) => {
      types.add(edge.type);
    });

    return Array.from(types);
  }, [graphData]);

  // useEffect para manejar el resize y re-renderizar el grafo
  useEffect(() => {
    let resizeTimer;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        setDimensions({ width: window.innerWidth, height: window.innerHeight });
      }, 250); // Debounce de 250ms
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, []);

  // useEffect para ajustar el grafo cuando se carga o cambian los datos
  useEffect(() => {
    if (networkRef.current && graph.nodes && graph.nodes.length > 0) {
      setTimeout(() => {
        networkRef.current.fit();
      }, 100);
    }
  }, [graph]);

  // useEffect para forzar re-render cuando cambian los datos del grafo
  // COMENTADO: Esto puede causar renders múltiples
  // useEffect(() => {
  //   if (graphData && graphData.nodes && graphData.nodes.length > 0) {
  //     console.log("GraphData changed, forcing re-render");
  //     setGraphKey(prev => prev + 1);
  //   }
  // }, [graphData]);

  if (!graph.nodes || graph.nodes.length === 0) {
    return (
      <div
        className="flex items-center justify-center"
        style={{
          width: environment === "PROD" ? "100vw" : "100%",
          height: environment === "PROD" ? "100vh" : 600,
          backgroundColor: colorBackground || "#FFFFFF",
          position: environment === "PROD" ? "fixed" : "relative",
          top: environment === "PROD" ? 0 : "auto",
          left: environment === "PROD" ? 0 : "auto",
          margin: 0,
          padding: 0,
        }}
      >
        <p>Data Not Found</p>
      </div>
    );
  }

  return (
    <div
      className={environment === "PROD" ? "" : "bg-transparent"}
      style={{
        width: environment === "PROD" ? "100vw" : "100%",
        height: environment === "PROD" ? "100vh" : 600,
        backgroundColor: colorBackground || "#FFFFFF",
        position: environment === "PROD" ? "fixed" : "relative",
        top: environment === "PROD" ? 0 : "auto",
        left: environment === "PROD" ? 0 : "auto",
        margin: 0,
        padding: 0,
        overflow: "hidden",
      }}
    >
      <Graph
        key={graphKey}
        graph={graph}
        options={options}
        events={events}
        style={{
          height: "100%",
          width: "100%",
          position: "absolute",
          top: 0,
          left: 0
        }}
        getNetwork={(network) => {
          networkRef.current = network;
          if (network) {
            setTimeout(() => {
              network.fit({
                animation: {
                  duration: 500,
                  easingFunction: "easeInOutQuad"
                }
              });
            }, 100);
          }
        }}
      />

      {/* Tooltip personalizado */}
      {tooltip.visible && (
        <div
          style={{
            position: "absolute",
            left: tooltip.x + 10,
            top: tooltip.y + 10,
            backgroundColor: "rgba(0, 0, 0, 0.85)",
            color: "white",
            padding: "8px 12px",
            borderRadius: "4px",
            fontSize: "12px",
            whiteSpace: "pre-line",
            zIndex: 10000,
            pointerEvents: "none",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
          }}
        >
          {tooltip.content}
        </div>
      )}
    </div>
  );
};
